import React, { useState } from 'react';
import './App.css';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import rawData from './sp500_data.json';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function App() {
  const [initialCapital, setInitialCapital] = useState('');
  const [isStarted, setIsStarted] = useState(false);

  // --- Simulation State ---
  const [dayIndex, setDayIndex] = useState(0);
  const [currentAssets, setCurrentAssets] = useState(0);
  const [assetHistory, setAssetHistory] = useState<number[]>([]);
  const [lastGainLoss, setLastGainLoss] = useState(0);

  const handleStart = () => {
    const capital = parseInt(initialCapital, 10);
    if (isNaN(capital) || capital <= 0) {
      alert('有効な初期資産を入力してください。');
      return;
    }
    setCurrentAssets(capital);
    setAssetHistory([capital]);
    setIsStarted(true);
  };

  const handleNextDay = () => {
    if (dayIndex >= rawData.length) return; // No more data

    const changePercent = rawData[dayIndex].change;
    const newAssets = currentAssets * (1 + changePercent / 100);
    const gainLoss = newAssets - currentAssets;

    setLastGainLoss(gainLoss);
    setCurrentAssets(newAssets);
    setAssetHistory([...assetHistory, newAssets]);
    setDayIndex(dayIndex + 1);
  };

  const isFinished = dayIndex >= rawData.length;

  // --- Chart Data ---
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: '総資産の推移',
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function(value: string | number) {
            return value.toLocaleString() + '円';
          }
        }
      }
    }
  };

  const chartData = {
    labels: ['Start', ...rawData.slice(0, dayIndex).map(d => d.date)],
    datasets: [
      {
        label: '総資産',
        data: assetHistory,
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
    ],
  };

  return (
    <div className="container mt-4">
      <header className="text-center mb-4">
        <h1>コロナショック株価暴落シミュレーター</h1>
      </header>

      {!isStarted ? (
        // --- 設定画面 ---
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">初期設定</h5>
                <div className="mb-3">
                  <label htmlFor="initialCapital" className="form-label">初期資産（円）</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="form-control"
                    id="initialCapital"
                    value={initialCapital}
                    onChange={(e) => setInitialCapital(e.target.value)}
                    placeholder='例: 1000000'
                  />
                </div>
                <button className="btn btn-primary w-100" onClick={handleStart}>
                  シミュレーション開始
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // --- シミュレーション画面 ---
        <div>
          <div className="row">
            {/* --- チャート表示エリア --- */}
            <div className="col-md-8">
              <div className="card">
                <div className="card-body">
                   <Line options={chartOptions} data={chartData} />
                </div>
              </div>
            </div>

            {/* --- 情報表示 & 操作エリア --- */}
            <div className="col-md-4">
              <div className="card mb-3">
                <div className="card-header">シミュレーション情報</div>
                <ul className="list-group list-group-flush">
                  <li className="list-group-item">日付: {dayIndex > 0 ? rawData[dayIndex - 1].date : '-'}</li>
                  <li className="list-group-item">本日の変動率: <span className={(rawData[dayIndex-1]?.change || 0) < 0 ? 'text-danger' : 'text-success'}>{(rawData[dayIndex-1]?.change || 0).toFixed(2)}%</span></li>
                  <li className="list-group-item">増減額: <span className={lastGainLoss < 0 ? 'text-danger' : 'text-success'}>{Math.round(lastGainLoss).toLocaleString()}円</span></li>
                  <li className="list-group-item"><b>総資産: {Math.round(currentAssets).toLocaleString()}円</b></li>
                </ul>
              </div>
              <div className="d-grid gap-2">
                {!isFinished ? (
                  <button className="btn btn-info" onClick={handleNextDay}>
                    次の日へ ({dayIndex + 1} / {rawData.length})
                  </button>
                ) : (
                  <div className="alert alert-success" role="alert">
                    シミュレーション終了です！
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
